<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import api from 'dashboard/api/aceleres/iaRodizios';
import { useAlert } from 'dashboard/composables';
import MaterialFormModal from './components/MaterialFormModal.vue';
import EventoFormModal from './components/EventoFormModal.vue';
import CursoFormModal from './components/CursoFormModal.vue';
import WorkingHoursEditor from './components/WorkingHoursEditor.vue';
import AutomationConfigurationCards from './components/AutomationConfigurationCards.vue';
import NomeIa from '../account/components/NomeIa.vue';

const props = defineProps({
  settings: { type: Object, required: true },
  isSaving: { type: Boolean, default: false },
  // Aceleres — Fase 1: quando vem preenchido, estamos no modal de uma equipe.
  teamId: { type: [Number, String], default: null },
});
const emit = defineEmits(['save', 'refresh']);
const route = useRoute();
const router = useRouter();
const captainText = {
  agentTitle: 'Agente do Captain',
  active: 'Ativa',
  configure: 'Configurar',
  persona: 'Nome e persona da IA da equipe',
  personaHelper:
    'O nome e o comportamento são definidos no agente selecionado. Use “Editar agente” para alterá-los.',
  selectAssistant: '— Selecione um agente do Captain —',
  editAssistant: 'Editar agente',
  createAssistant: 'Criar agente',
  openInNewTab: 'Abre o Captain em uma nova aba',
  inboxes: 'Caixas atendidas por este agente',
  noInboxes:
    'Esta equipe ainda não possui caixas vinculadas. Conecte uma caixa à equipe antes de ativar a IA.',
  legacyNotice:
    'Há uma caixa salva no formato antigo. Salve a conexão para criar o vínculo nativo do Captain.',
  mixedNotice:
    'As caixas usam agentes diferentes. Selecione o agente desejado e confirme a substituição para unificar.',
  saveConnection: 'Salvar conexão',
  sharedContext: 'Contexto compartilhado',
  ownContext: '— Usar o contexto próprio desta equipe —',
  noContextTeam: 'Nenhuma outra equipe disponível para compartilhar contexto.',
  contextHelper:
    'Materiais, eventos, cursos e instruções podem vir de outra equipe. Agente, ativação, horário e rodízio continuam sendo próprios desta equipe.',
};

const aiEnabled = ref(props.settings.ai_enabled);
const aiInboxId = ref(props.settings.ai_inbox_id);
const aiContext = ref(props.settings.ai_context || '');
const aiContextSourceTeamId = ref(
  props.settings.ai_context_source_team_id ??
    props.settings.ai_agent_source_team_id ??
    null
);
const workingHours = ref([...(props.settings.global_working_hours || [])]);
const captainConfiguration = ref(props.settings.captain_configuration || {});
const selectedCaptainAssistantId = ref(null);
const selectedCaptainInboxIds = ref([]);
const isSavingCaptain = ref(false);
const shouldRefreshCaptainOnReturn = ref(false);

const syncCaptainConfiguration = settings => {
  const configuration = settings.captain_configuration || {};
  const assistants = configuration.assistants || [];
  const linkedInboxIds = configuration.linked_inbox_ids || [];
  const legacyInboxId = configuration.legacy_inbox_id;

  captainConfiguration.value = configuration;
  selectedCaptainAssistantId.value =
    configuration.effective_assistant_id ??
    (assistants.length === 1 ? assistants[0].id : null);
  selectedCaptainInboxIds.value = [];
  if (linkedInboxIds.length) {
    selectedCaptainInboxIds.value = [...linkedInboxIds];
  } else if (legacyInboxId) {
    selectedCaptainInboxIds.value = [legacyInboxId];
  }
};

syncCaptainConfiguration(props.settings);

// Sincroniza refs locais quando o pai (Index.vue) atualiza settings após o fetch.
// Sem isso, refs locais ficam congeladas no estado inicial (DEFAULT_SETTINGS).
watch(
  () => props.settings,
  s => {
    aiEnabled.value = s.ai_enabled;
    aiInboxId.value = s.ai_inbox_id;
    aiContext.value = s.ai_context || '';
    aiContextSourceTeamId.value =
      s.ai_context_source_team_id ?? s.ai_agent_source_team_id ?? null;
    workingHours.value = [...(s.global_working_hours || [])];
    syncCaptainConfiguration(s);
  },
  { deep: true }
);

const saveWorkingHours = () =>
  emit('save', { global_working_hours: workingHours.value });

const inboxes = computed(() => props.settings.inboxes || []);
// Outras equipes da conta — vindas do endpoint team-scoped (settings.teams).
const teams = computed(() => props.settings.teams || []);
// Estável: baseado no teamId (não muda enquanto o modal tá aberto), não no
// fetch. Assim a seção não pisca quando o settings é recarregado num salvar.
const isTeamScoped = computed(
  () => props.teamId != null && props.teamId !== ''
);
const captainAssistants = computed(
  () => captainConfiguration.value.assistants || []
);
const captainInboxes = computed(() => captainConfiguration.value.inboxes || []);
const selectedCaptainAssistant = computed(() =>
  captainAssistants.value.find(
    assistant => assistant.id === selectedCaptainAssistantId.value
  )
);
const hasCaptainBinding = computed(
  () => (captainConfiguration.value.linked_inbox_ids || []).length > 0
);
const captainStatusLabel = computed(() => {
  const labels = {
    active: 'Ativa e dentro do horário',
    outside_hours: 'Ativa, fora do horário configurado',
    disabled: 'Desativada',
    missing_binding: 'Ativação incompleta: falta conectar o Captain',
    mixed: 'Configuração divergente entre caixas',
    unavailable: 'Captain indisponível nesta instalação',
  };
  return labels[captainConfiguration.value.status] || 'Configuração nativa';
});

const onToggle = v => {
  if (v && isTeamScoped.value && !hasCaptainBinding.value) {
    useAlert(
      'Conecte um agente do Captain a pelo menos uma caixa antes de ativar a IA'
    );
    return;
  }

  aiEnabled.value = v;
  emit('save', { ai_enabled: v });
};

const saveInbox = () => emit('save', { ai_inbox_id: aiInboxId.value });
const saveContextSource = () =>
  emit('save', { ai_context_source_team_id: aiContextSourceTeamId.value });
const saveContext = () => emit('save', { ai_context: aiContext.value });

const saveCaptainConfiguration = async (replaceConflicts = false) => {
  if (
    selectedCaptainAssistantId.value &&
    !selectedCaptainInboxIds.value.length &&
    !hasCaptainBinding.value
  ) {
    useAlert('Selecione ao menos uma caixa da equipe para conectar o agente');
    return;
  }

  isSavingCaptain.value = true;
  try {
    const { data } = await api.updateCaptainConfiguration(props.teamId, {
      assistant_id: selectedCaptainAssistantId.value,
      inbox_ids: selectedCaptainInboxIds.value,
      replace_conflicts: replaceConflicts,
    });
    syncCaptainConfiguration({
      ...props.settings,
      ai_enabled: data.ai_enabled,
      captain_configuration: data,
    });
    aiEnabled.value = data.ai_enabled;
    useAlert('Conexão com o Captain atualizada');
  } catch (error) {
    if (error?.response?.status === 409 && !replaceConflicts) {
      const conflicts = error.response.data?.conflicts || [];
      const names = conflicts
        .map(item => item.name)
        .filter(Boolean)
        .join(', ');
      const confirmed = window.confirm(
        `As caixas ${names || 'selecionadas'} já usam outro agente. Substituir os vínculos?`
      );
      if (confirmed) await saveCaptainConfiguration(true);
      return;
    }
    useAlert(
      error?.response?.data?.error ||
        'Erro ao atualizar conexão com o Captain'
    );
  } finally {
    isSavingCaptain.value = false;
  }
};

const openCaptainAssistant = () => {
  const assistantId = selectedCaptainAssistantId.value;
  const captainRoute = assistantId
    ? {
        name: 'captain_assistants_settings_index',
        params: { accountId: route.params.accountId, assistantId },
      }
    : {
        name: 'captain_assistants_create_index',
        params: { accountId: route.params.accountId },
      };

  window.open(
    router.resolve(captainRoute).href,
    '_blank',
    'noopener,noreferrer'
  );

  window.setTimeout(() => {
    shouldRefreshCaptainOnReturn.value = true;
  }, 0);
};

const refreshCaptainOnReturn = () => {
  if (
    !shouldRefreshCaptainOnReturn.value ||
    document.visibilityState !== 'visible'
  ) {
    return;
  }

  shouldRefreshCaptainOnReturn.value = false;
  emit('refresh');
};

// ----------- Materiais (Contexto da IA, igual ao AI Intelligence do Aceleres) ----------
// Aceleres tem 2 subseções distintas: "Atendimento — Zoraxy" e "Vendedores — LOGOS"
const materiais = ref([]);
const isLoadingMateriais = ref(false);
const materialModalOpen = ref(false);
const materialBeingEdited = ref(null);
const materialDefaultSection = ref('zoraxy');

const materiaisZoraxy = computed(() => materiais.value.filter(m => m.section === 'zoraxy'));
const materiaisLogos = computed(() => materiais.value.filter(m => m.section === 'logos'));

const fetchMateriais = async () => {
  isLoadingMateriais.value = true;
  try {
    const { data } = await api.listMateriais(null, props.teamId);
    materiais.value = data.data || [];
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao carregar materiais');
  } finally {
    isLoadingMateriais.value = false;
  }
};

const openNewMaterial = (section = 'zoraxy') => {
  materialBeingEdited.value = null;
  materialDefaultSection.value = section;
  materialModalOpen.value = true;
};
const openEditMaterial = async material => {
  try {
    const { data } = await api.getMaterial(material.id, props.teamId);
    materialBeingEdited.value = data;
    materialModalOpen.value = true;
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao abrir material');
  }
};
const onMaterialSaved = () => {
  materialModalOpen.value = false;
  fetchMateriais();
};
const removeMaterial = async material => {
  if (!confirm(`Excluir o material "${material.title}"?`)) return;
  try {
    await api.deleteMaterial(material.id, props.teamId);
    useAlert('Material removido');
    fetchMateriais();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao remover material');
  }
};
const toggleMaterialActive = async material => {
  try {
    await api.updateMaterial(material.id, { is_active: !material.is_active }, props.teamId);
    fetchMateriais();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao atualizar material');
  }
};

// ----------- Eventos ----------
const eventos = ref([]);
const isLoadingEventos = ref(false);
const eventoModalOpen = ref(false);
const eventoBeingEdited = ref(null);

const fetchEventos = async () => {
  isLoadingEventos.value = true;
  try {
    const { data } = await api.listEventos(props.teamId);
    eventos.value = data.data || [];
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao carregar eventos');
  } finally {
    isLoadingEventos.value = false;
  }
};
const openNewEvento = () => {
  eventoBeingEdited.value = null;
  eventoModalOpen.value = true;
};
const openEditEvento = evento => {
  eventoBeingEdited.value = { ...evento };
  eventoModalOpen.value = true;
};
const onEventoSaved = () => {
  eventoModalOpen.value = false;
  fetchEventos();
};
const removeEvento = async evento => {
  if (!confirm(`Excluir o evento "${evento.name}"?`)) return;
  try {
    await api.deleteEvento(evento.id, props.teamId);
    useAlert('Evento removido');
    fetchEventos();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao remover evento');
  }
};

// ----------- Cursos ----------
const cursos = ref([]);
const isLoadingCursos = ref(false);
const cursoModalOpen = ref(false);
const cursoBeingEdited = ref(null);

const fetchCursos = async () => {
  isLoadingCursos.value = true;
  try {
    const { data } = await api.listCursos(props.teamId);
    cursos.value = data.data || [];
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao carregar cursos');
  } finally {
    isLoadingCursos.value = false;
  }
};
const openNewCurso = () => {
  cursoBeingEdited.value = null;
  cursoModalOpen.value = true;
};
const openEditCurso = curso => {
  cursoBeingEdited.value = { ...curso };
  cursoModalOpen.value = true;
};
const onCursoSaved = () => {
  cursoModalOpen.value = false;
  fetchCursos();
};
const removeCurso = async curso => {
  if (!confirm(`Excluir o curso "${curso.name}"?`)) return;
  try {
    await api.deleteCurso(curso.id, props.teamId);
    useAlert('Curso removido');
    fetchCursos();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao remover curso');
  }
};

const fmtDate = d => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch (e) {
    return d;
  }
};
const fmtDay = d => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('pt-BR');
  } catch (e) {
    return d;
  }
};
const fmtMoney = v => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

onMounted(() => {
  fetchMateriais();
  fetchEventos();
  fetchCursos();
  window.addEventListener('focus', refreshCaptainOnReturn);
  document.addEventListener('visibilitychange', refreshCaptainOnReturn);
});

onBeforeUnmount(() => {
  window.removeEventListener('focus', refreshCaptainOnReturn);
  document.removeEventListener('visibilitychange', refreshCaptainOnReturn);
});
</script>

<template>
  <div class="flex flex-col gap-6 w-full">
    <!-- Configuração Laravel legada: somente no contexto global. -->
    <NomeIa v-if="!isTeamScoped" :team-id="props.teamId" />

    <AutomationConfigurationCards
      v-if="isTeamScoped"
      type="ai"
      :team-id="props.teamId"
      @changed="emit('refresh')"
    />

    <section v-if="isTeamScoped" class="border border-n-strong bg-n-background rounded-lg p-5 flex flex-col gap-1.5">
      <label class="text-body-small text-n-slate-12 font-medium">
        {{ captainText.sharedContext }}
      </label>
      <select
        v-if="teams.length"
        v-model="aiContextSourceTeamId"
        class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12"
        @change="saveContextSource"
      >
        <option :value="null">{{ captainText.ownContext }}</option>
        <option v-for="t in teams" :key="t.id" :value="t.id">{{ t.name }}</option>
      </select>
      <p v-else class="text-body-small text-n-slate-11 m-0">
        {{ captainText.noContextTeam }}
      </p>
      <p v-if="teams.length" class="text-body-small text-n-slate-11 m-0">
        {{ captainText.contextHelper }}
      </p>
    </section>

    <!-- Toggle + Captain -->
    <section v-if="false" class="border border-n-strong bg-n-background rounded-lg p-5 flex flex-col gap-4">
      <div class="flex items-center justify-between gap-4">
        <div>
          <h2 class="text-body-large text-n-slate-12 font-medium m-0">Atendimento por IA</h2>
          <p class="text-body-small text-n-slate-11 m-0">
            Quando ativo, a IA responde leads conforme o horário e o contexto configurados abaixo.
          </p>
        </div>
        <Switch :model-value="aiEnabled" @update:model-value="onToggle" />
      </div>

      <div
        v-if="isTeamScoped"
        class="flex flex-col gap-4 border-t border-n-weak pt-4"
      >
        <div class="flex items-center justify-between gap-3">
          <div class="min-w-0">
            <h3 class="text-body-medium text-n-slate-12 font-medium m-0">
              {{ captainText.agentTitle }}
            </h3>
            <p class="text-body-small text-n-slate-11 m-0">
              {{ captainStatusLabel }}
            </p>
          </div>
          <span
            class="inline-flex min-h-7 items-center rounded-md px-2.5 text-label-small font-medium"
            :class="
              captainConfiguration.status === 'active'
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-slate-3 text-n-slate-11'
            "
          >
            {{
              captainConfiguration.status === 'active'
                ? captainText.active
                : captainText.configure
            }}
          </span>
        </div>

        <div
          v-if="captainConfiguration.available"
          class="flex flex-col gap-1.5"
        >
          <label class="text-body-small text-n-slate-12 font-medium">
            {{ captainText.persona }}
          </label>
          <div class="flex flex-col sm:flex-row gap-2">
            <select
              v-model="selectedCaptainAssistantId"
              class="min-h-10 flex-1 border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12"
            >
              <option :value="null">{{ captainText.selectAssistant }}</option>
              <option
                v-for="assistant in captainAssistants"
                :key="assistant.id"
                :value="assistant.id"
              >
                {{ assistant.name }}
              </option>
            </select>
            <Button
              :label="
                selectedCaptainAssistantId
                  ? captainText.editAssistant
                  : captainText.createAssistant
              "
              icon="i-lucide-external-link"
              :title="captainText.openInNewTab"
              variant="outline"
              @click="openCaptainAssistant"
            />
          </div>
          <p class="text-body-small text-n-slate-11 m-0">
            {{ captainText.personaHelper }}
          </p>
          <p
            v-if="selectedCaptainAssistant?.description"
            class="text-body-small text-n-slate-11 m-0"
          >
            {{ selectedCaptainAssistant.description }}
          </p>
        </div>

        <div v-if="captainInboxes.length" class="flex flex-col gap-2">
          <label class="text-body-small text-n-slate-12 font-medium">
            {{ captainText.inboxes }}
          </label>
          <div class="divide-y divide-n-weak rounded-md border border-n-strong">
            <label
              v-for="inbox in captainInboxes"
              :key="inbox.id"
              class="flex min-h-11 items-center gap-3 px-3 py-2 cursor-pointer"
            >
              <input
                v-model="selectedCaptainInboxIds"
                type="checkbox"
                :value="inbox.id"
                class="size-4 accent-n-teal-9"
              />
              <span class="min-w-0 flex-1 text-body-small text-n-slate-12 truncate">
                {{ inbox.name }}
              </span>
              <span
                v-if="inbox.captain_assistant_name"
                class="text-label-small text-n-slate-11"
              >
                {{ inbox.captain_assistant_name }}
              </span>
            </label>
          </div>
        </div>
        <p v-else class="text-body-small text-n-slate-11 m-0">
          {{ captainText.noInboxes }}
        </p>

        <p
          v-if="captainConfiguration.legacy_inbox_id && !hasCaptainBinding"
          class="text-body-small text-n-amber-11 m-0"
        >
          {{ captainText.legacyNotice }}
        </p>
        <p
          v-if="captainConfiguration.mixed_assistants"
          class="text-body-small text-n-amber-11 m-0"
        >
          {{ captainText.mixedNotice }}
        </p>

        <div class="flex justify-end">
          <Button
            :label="captainText.saveConnection"
            icon="i-lucide-link"
            :is-loading="isSavingCaptain"
            @click="saveCaptainConfiguration()"
          />
        </div>
      </div>

      <div v-if="!isTeamScoped" class="flex flex-col gap-1.5">
        <label class="text-body-small text-n-slate-12 font-medium">Instância da IA</label>
        <select
          v-model="aiInboxId"
          class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12"
          @change="saveInbox"
        >
          <option :value="null">— Selecione uma caixa de entrada —</option>
          <option v-for="ib in inboxes" :key="ib.id" :value="ib.id">{{ ib.name }}</option>
        </select>
        <p class="text-body-small text-n-slate-11 m-0">
          Caixa de entrada que a IA usará para atender clientes.
        </p>
      </div>

      <div v-if="isTeamScoped" class="flex flex-col gap-1.5 border-t border-n-weak pt-4">
        <label class="text-body-small text-n-slate-12 font-medium">
          {{ captainText.sharedContext }}
        </label>
        <select
          v-if="teams.length"
          v-model="aiContextSourceTeamId"
          class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12"
          @change="saveContextSource"
        >
          <option :value="null">{{ captainText.ownContext }}</option>
          <option v-for="t in teams" :key="t.id" :value="t.id">{{ t.name }}</option>
        </select>
        <p v-else class="text-body-small text-n-slate-11 m-0">
          {{ captainText.noContextTeam }}
        </p>
        <p v-if="teams.length" class="text-body-small text-n-slate-11 m-0">
          {{ captainText.contextHelper }}
        </p>
      </div>
    </section>

    <!-- Horário de atendimento -->
    <section v-if="false" class="border border-n-strong bg-n-background rounded-lg p-5 flex flex-col gap-3">
      <header>
        <h2 class="text-body-large text-n-slate-12 font-medium m-0">Horário de atendimento</h2>
        <p class="text-body-small text-n-slate-11 m-0">
          Quando a IA está habilitada, ela atende dentro deste horário. Compartilhado com o Rodízio.
        </p>
      </header>
      <WorkingHoursEditor v-model="workingHours" title="" helper="" />
      <div class="flex justify-end">
        <Button label="Salvar horário" :is-loading="isSaving" @click="saveWorkingHours" />
      </div>
    </section>

    <!-- Contexto da IA (textarea simples + materiais) -->
    <section class="border border-n-strong bg-n-background rounded-lg p-5 flex flex-col gap-4">
      <header class="flex items-center justify-between gap-4">
        <div>
          <h2 class="text-body-large text-n-slate-12 font-medium m-0">Contexto da IA</h2>
          <p class="text-body-small text-n-slate-11 m-0">
            Instruções gerais + materiais (textos e arquivos) que a IA consulta ao responder.
          </p>
        </div>
      </header>
      <div class="flex flex-col gap-1.5">
        <label class="text-body-small text-n-slate-12 font-medium">Instruções gerais</label>
        <textarea
          v-model="aiContext"
          rows="6"
          class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12"
          placeholder="Ex.: Você é a assistente Aurora da Aceleres…"
        />
        <div class="flex justify-end">
          <Button label="Salvar contexto" :is-loading="isSaving" @click="saveContext" />
        </div>
      </div>

      <!-- Subseção: Atendimento — Zoraxy -->
      <div class="flex flex-col gap-3 mt-2">
        <div class="flex items-center justify-between">
          <h3 class="text-body-medium text-n-slate-12 font-medium m-0">Atendimento — Zoraxy</h3>
          <Button label="+ Novo material" variant="outline" @click="openNewMaterial('zoraxy')" />
        </div>
        <div v-if="isLoadingMateriais" class="text-body-small text-n-slate-11">Carregando…</div>
        <div v-else-if="!materiaisZoraxy.length" class="text-body-small text-n-slate-11 text-center py-6 border border-dashed border-n-strong rounded">
          Nenhum material cadastrado nesta seção.
        </div>
        <table v-else class="w-full text-body-small">
          <thead>
            <tr class="text-left text-n-slate-11">
              <th class="py-2 pr-2">Título</th>
              <th class="py-2 pr-2">Tipo</th>
              <th class="py-2 pr-2 w-20 text-center">Prioridade</th>
              <th class="py-2 pr-2 w-20 text-center">Ativo</th>
              <th class="py-2 w-32" />
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in materiaisZoraxy" :key="m.id" class="border-t border-n-strong">
              <td class="py-2 pr-2 text-n-slate-12">
                {{ m.title }}
                <span v-if="m.has_file" class="ml-1 text-n-slate-11 text-label-small">
                  ({{ m.file_name }})
                </span>
              </td>
              <td class="py-2 pr-2 text-n-slate-11">{{ m.source_type === 'file' ? 'Arquivo' : 'Texto' }}</td>
              <td class="py-2 pr-2 text-center">{{ m.priority }}</td>
              <td class="py-2 pr-2 text-center">
                <Switch :model-value="m.is_active" @update:model-value="toggleMaterialActive(m)" />
              </td>
              <td class="py-2 text-right">
                <button class="text-n-blue-9 hover:underline mr-3" @click="openEditMaterial(m)">Editar</button>
                <button class="text-n-ruby-9 hover:underline" @click="removeMaterial(m)">Remover</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Subseção: Vendedores — LOGOS -->
      <div class="flex flex-col gap-3 mt-4 pt-4 border-t border-n-strong">
        <div class="flex items-center justify-between">
          <h3 class="text-body-medium text-n-slate-12 font-medium m-0">Vendedores — LOGOS</h3>
          <Button label="+ Novo material" variant="outline" @click="openNewMaterial('logos')" />
        </div>
        <div v-if="isLoadingMateriais" class="text-body-small text-n-slate-11">Carregando…</div>
        <div v-else-if="!materiaisLogos.length" class="text-body-small text-n-slate-11 text-center py-6 border border-dashed border-n-strong rounded">
          Nenhum material cadastrado nesta seção.
        </div>
        <table v-else class="w-full text-body-small">
          <thead>
            <tr class="text-left text-n-slate-11">
              <th class="py-2 pr-2">Título</th>
              <th class="py-2 pr-2">Tipo</th>
              <th class="py-2 pr-2 w-20 text-center">Prioridade</th>
              <th class="py-2 pr-2 w-20 text-center">Ativo</th>
              <th class="py-2 w-32" />
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in materiaisLogos" :key="m.id" class="border-t border-n-strong">
              <td class="py-2 pr-2 text-n-slate-12">
                {{ m.title }}
                <span v-if="m.has_file" class="ml-1 text-n-slate-11 text-label-small">
                  ({{ m.file_name }})
                </span>
              </td>
              <td class="py-2 pr-2 text-n-slate-11">{{ m.source_type === 'file' ? 'Arquivo' : 'Texto' }}</td>
              <td class="py-2 pr-2 text-center">{{ m.priority }}</td>
              <td class="py-2 pr-2 text-center">
                <Switch :model-value="m.is_active" @update:model-value="toggleMaterialActive(m)" />
              </td>
              <td class="py-2 text-right">
                <button class="text-n-blue-9 hover:underline mr-3" @click="openEditMaterial(m)">Editar</button>
                <button class="text-n-ruby-9 hover:underline" @click="removeMaterial(m)">Remover</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Eventos -->
    <section class="border border-n-strong bg-n-background rounded-lg p-5 flex flex-col gap-3">
      <header class="flex items-center justify-between gap-4">
        <div>
          <h2 class="text-body-large text-n-slate-12 font-medium m-0">Contexto de Eventos</h2>
          <p class="text-body-small text-n-slate-11 m-0">
            Eventos consumidos pela IA até a data de expiração. Após expirar, deixam de ser considerados.
          </p>
        </div>
        <Button label="+ Novo evento" variant="outline" @click="openNewEvento" />
      </header>
      <div v-if="isLoadingEventos" class="text-body-small text-n-slate-11">Carregando…</div>
      <div v-else-if="!eventos.length" class="text-body-small text-n-slate-11 text-center py-6 border border-dashed border-n-strong rounded">
        Nenhum evento cadastrado.
      </div>
      <table v-else class="w-full text-body-small">
        <thead>
          <tr class="text-left text-n-slate-11">
            <th class="py-2 pr-2">Nome</th>
            <th class="py-2 pr-2">Arquivo</th>
            <th class="py-2 pr-2">Expira em</th>
            <th class="py-2 pr-2 w-20 text-center">Ativo</th>
            <th class="py-2 w-32" />
          </tr>
        </thead>
        <tbody>
          <tr v-for="e in eventos" :key="e.id" class="border-t border-n-strong" :class="e.expired ? 'opacity-60' : ''">
            <td class="py-2 pr-2 text-n-slate-12">{{ e.name }}</td>
            <td class="py-2 pr-2 text-n-slate-11">
              <a v-if="e.has_file && e.file_url" :href="e.file_url" target="_blank" rel="noopener" class="text-n-blue-9 hover:underline">
                {{ e.file_name }}
              </a>
              <span v-else>—</span>
            </td>
            <td class="py-2 pr-2 text-n-slate-11">
              {{ fmtDate(e.expires_at) }}
              <span v-if="e.expired" class="ml-2 text-n-ruby-9 text-label-small">(expirado)</span>
            </td>
            <td class="py-2 pr-2 text-center">{{ e.is_active ? 'Sim' : 'Não' }}</td>
            <td class="py-2 text-right">
              <button class="text-n-blue-9 hover:underline mr-3" @click="openEditEvento(e)">Editar</button>
              <button class="text-n-ruby-9 hover:underline" @click="removeEvento(e)">Remover</button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <!-- Cursos -->
    <section class="border border-n-strong bg-n-background rounded-lg p-5 flex flex-col gap-3">
      <header class="flex items-center justify-between gap-4">
        <div>
          <h2 class="text-body-large text-n-slate-12 font-medium m-0">Contexto de Cursos</h2>
          <p class="text-body-small text-n-slate-11 m-0">
            Cursos que a IA pode oferecer aos clientes (nome, datas, valor, descrição e vagas).
          </p>
        </div>
        <Button label="+ Novo curso" variant="outline" @click="openNewCurso" />
      </header>
      <div v-if="isLoadingCursos" class="text-body-small text-n-slate-11">Carregando…</div>
      <div v-else-if="!cursos.length" class="text-body-small text-n-slate-11 text-center py-6 border border-dashed border-n-strong rounded">
        Nenhum curso cadastrado.
      </div>
      <table v-else class="w-full text-body-small">
        <thead>
          <tr class="text-left text-n-slate-11">
            <th class="py-2 pr-2">Nome</th>
            <th class="py-2 pr-2">Datas</th>
            <th class="py-2 pr-2">Valor</th>
            <th class="py-2 pr-2 w-20 text-center">Vagas</th>
            <th class="py-2 w-32" />
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in cursos" :key="c.id" class="border-t border-n-strong">
            <td class="py-2 pr-2 text-n-slate-12">{{ c.name }}</td>
            <td class="py-2 pr-2 text-n-slate-11">
              {{ fmtDay(c.start_date) }}
              <template v-if="c.end_date">→ {{ fmtDay(c.end_date) }}</template>
            </td>
            <td class="py-2 pr-2 text-n-slate-11">{{ fmtMoney(c.value) }}</td>
            <td class="py-2 pr-2 text-center">{{ c.vagas ?? '—' }}</td>
            <td class="py-2 text-right">
              <button class="text-n-blue-9 hover:underline mr-3" @click="openEditCurso(c)">Editar</button>
              <button class="text-n-ruby-9 hover:underline" @click="removeCurso(c)">Remover</button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <MaterialFormModal
      v-if="materialModalOpen"
      :material="materialBeingEdited"
      :default-section="materialDefaultSection"
      :team-id="props.teamId"
      @close="materialModalOpen = false"
      @saved="onMaterialSaved"
    />
    <EventoFormModal
      v-if="eventoModalOpen"
      :evento="eventoBeingEdited"
      :team-id="props.teamId"
      @close="eventoModalOpen = false"
      @saved="onEventoSaved"
    />
    <CursoFormModal
      v-if="cursoModalOpen"
      :curso="cursoBeingEdited"
      :team-id="props.teamId"
      @close="cursoModalOpen = false"
      @saved="onCursoSaved"
    />
  </div>
</template>
